import { ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../lib/utils';

type ButtonVariant = 'primary' | 'secondary' | 'plain' | 'success' | 'critical';
type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  icon?: React.ReactNode;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-tf-primary text-white hover:bg-tf-primary-hover shadow-sm focus:ring-tf-ring',
  success: 'bg-tf-success text-white hover:bg-success-dark shadow-sm focus:ring-tf-success',
  critical: 'bg-tf-critical text-white hover:bg-critical-dark shadow-sm focus:ring-tf-critical',
  secondary: 'bg-tf-bg-card text-tf-text border border-tf-border hover:bg-tf-bg-hover shadow-sm focus:ring-tf-ring',
  plain: 'text-tf-primary hover:bg-tf-primary-light focus:ring-tf-ring',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm min-h-[32px]',
  md: 'px-4 py-2 text-sm min-h-[36px]',
  lg: 'px-5 py-2.5 text-base min-h-[40px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    children, 
    variant = 'secondary', 
    size = 'md',
    fullWidth = false,
    icon,
    className,
    disabled,
    ...props 
  }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-all duration-200',
          'focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
          variantStyles[variant],
          sizeStyles[size],
          fullWidth && 'w-full',
          className
        )}
        disabled={disabled}
        {...props}
      >
        {icon && <span className="flex-shrink-0">{icon}</span>}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';













